import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { ActionState, MovementState } from '../base/Unit';
import { WalkerBehavior } from './WalkerBehavior';
import { FlyerBehavior } from './FlyerBehavior';
import { FlipBehavior } from './FlipBehavior';
import { Allegiance, LayerDepths } from '../../constants/enums';

export interface FirePoint {
  x: number;
  y: number;
}

export interface ShooterConfig extends BehaviorConfig {
  projectileType?: string;
  fireRate?: number;
  damage?: number;
  firePoints?: FirePoint[];
  projectileSpeed?: number;
  /** ImagePoint name to use as fire point (preferred over firePoints) */
  fireImagePoint?: string;
  /** Detection range for auto-shooting (from prefab's detectionRange) */
  detectionRange?: number;
  /** Enable automatic shooting when player is in range (default: true for enemies) */
  autoShoot?: boolean;
  /** Enable turning to face target (default: true) */
  turnable?: boolean;
  /** Minimum distance to keep from player before shooting (default: 0) */
  minRange?: number;
  /** Stop moving when shooting (default: true). If false, keeps moving while shooting */
  stopToShoot?: boolean;
  /** Chase the player while in detection range (default: false) */
  chaseWhileShooting?: boolean;
}

export interface ProjectileSpawnEvent {
  type: string;
  x: number;
  y: number;
  angle: number;
  damage: number;
  speed: number;
  allegiance: string;
  shooterId?: number | undefined;
}

/**
 * ShooterBehavior - Shooting capability for entities
 *
 * Used by: Soldier, Farm1, CyberScareCrow, RastaFarmer, etc.
 *
 * Features:
 * - Configurable fire rate
 * - Multiple fire points
 * - Different projectile types
 * - Target tracking
 */
export class ShooterBehavior extends Behavior {
  public projectileType: string;
  public fireRate: number;
  public damage: number;
  public firePoints: FirePoint[];
  public projectileSpeed: number;
  public lastFired: number = 0;
  public fireImagePoint?: string;

  // Auto-shoot properties
  public detectionRange: number;
  public autoShoot: boolean;
  public turnable: boolean;
  public minRange: number;
  public stopToShoot: boolean;
  public chaseWhileShooting: boolean;

  // State tracking
  private isGuarding: boolean = false;
  private currentTarget: Entity | null = null;

  constructor(entity: Entity, config: ShooterConfig = {}) {
    super(entity, config);

    // Validate required config - no fallbacks, no temporary values
    const missing: string[] = [];
    if (!config.projectileType) missing.push('projectileType');
    if (!config.fireRate) missing.push('fireRate');
    if (config.damage === undefined) missing.push('damage');
    if (!config.projectileSpeed) missing.push('projectileSpeed');

    if (missing.length > 0) {
      console.error(`[ShooterBehavior] Missing required config for entity "${entity.entityType}":`, missing);
      console.error('[ShooterBehavior] Stack trace:', new Error().stack);
      this.enabled = false;
      // Assign dummy values only to avoid undefined (but the behavior is disabled)
      this.projectileType = '';
      this.fireRate = Infinity;
      this.damage = 0;
      this.projectileSpeed = 0;
      this.firePoints = [];
      this.detectionRange = 0;
      this.autoShoot = false;
      this.turnable = false;
      this.minRange = 0;
      this.stopToShoot = false;
      this.chaseWhileShooting = false;
      return;
    }

    this.projectileType = config.projectileType;
    this.fireRate = config.fireRate;
    this.damage = config.damage;
    this.projectileSpeed = config.projectileSpeed;

    // Validate fire source: fireImagePoint OR firePoints required
    if (config.fireImagePoint) {
      // Use imagePoint for fire position (preferred for rotating entities)
      this.fireImagePoint = config.fireImagePoint;
      this.firePoints = []; // Not used when fireImagePoint is set
    } else if (config.firePoints && config.firePoints.length > 0) {
      // Use legacy firePoints array (static offsets)
      this.firePoints = config.firePoints;
    } else {
      // Neither provided - loud fail per CLAUDE.md rules
      console.error('[ShooterBehavior] Missing required config: fireImagePoint or firePoints', {
        entity: entity.entityType,
        fix: 'Add fireImagePoint (preferred) or firePoints to entities-overrides.json',
      });
      this.enabled = false;
      this.firePoints = [];
      this.detectionRange = 0;
      this.autoShoot = false;
      this.turnable = false;
      this.minRange = 0;
      this.stopToShoot = false;
      this.chaseWhileShooting = false;
      return;
    }

    // Auto-shoot config
    this.detectionRange = config.detectionRange ?? 0;
    this.autoShoot = config.autoShoot ?? (entity.allegiance === Allegiance.Enemy);
    this.turnable = config.turnable ?? true;
    this.minRange = config.minRange ?? 0;
    this.stopToShoot = config.stopToShoot ?? true;
    this.chaseWhileShooting = config.chaseWhileShooting ?? false;

    // Initialize namespaced state for behavior-to-behavior communication
    this.entity.state.behaviors.shooter = {
      isShooting: false,
      isGuarding: false,
    };
  }

  /**
   * Update namespaced state for other behaviors to read
   */
  private updateShooterState(): void {
    const state = this.entity.state.behaviors.shooter;
    if (state) {
      state.isGuarding = this.isGuarding;
    }
  }

  /**
   * Update - handles automatic shooting when player is in range
   */
  override update(time: number, _delta: number): void {
    if (!this.enabled) return;

    // Defense in depth: don't shoot if entity is dead
    const entityWithDead = this.entity as Entity & { isDead?: () => boolean };
    if (entityWithDead.isDead?.()) return;

    // Draw debug if enabled
    if (this.debugEnabled) {
      this.drawDebug();
    }

    // Check if TurretBehavior exists via namespaced state (behavior communication)
    const turretState = this.entity.state.behaviors.turret;

    if (turretState) {
      // TURRET MODE: TurretBehavior controls aiming, we just shoot when locked
      if (turretState.isAiming && this.canShoot(time)) {
        this.shootAtAngle(time, turretState.angle);
      }
      return; // Skip normal flip + auto-shoot logic
    }

    // NORMAL MODE: Standard auto-shoot behavior
    if (!this.autoShoot || this.detectionRange <= 0) {
      return;
    }

    // Get player from registry
    const player = this.scene.registry.get('player') as Entity | undefined;
    if (!player || !player.active) {
      this.currentTarget = null;
      this.exitGuardState();
      return;
    }

    // Calculate distance to player
    const distance = Phaser.Math.Distance.Between(
      this.entity.x, this.entity.y,
      player.x, player.y
    );

    // Check if player is in detection range
    if (distance <= this.detectionRange && distance >= this.minRange) {
      this.currentTarget = player;

      // Turn to face player if turnable (only if not chasing, chase handles direction)
      if (this.turnable && !this.chaseWhileShooting) {
        this.faceTarget(player);
      }

      // Handle movement based on config
      if (this.chaseWhileShooting) {
        // Chase the player while shooting
        this.startChasing(player);
      } else if (this.stopToShoot) {
        // Stop and guard (original behavior)
        this.enterGuardState();
      }
      // If neither, just shoot while continuing normal movement

      // Try to shoot
      if (this.canShoot(time)) {
        this.shootAt(time, player);
      }
    } else {
      // Player out of range
      this.currentTarget = null;
      if (this.chaseWhileShooting) {
        this.stopChasing();
      } else if (this.stopToShoot) {
        this.exitGuardState();
      }
    }
  }

  /**
   * Face the target (flip sprite horizontally)
   * Uses FlipBehavior.lookAt() if available to avoid conflicts
   */
  private faceTarget(target: Entity): void {
    // Use FlipBehavior if present to avoid conflicts with automatic flip modes
    const flipBehavior = this.entity.getBehavior(FlipBehavior);
    if (flipBehavior) {
      flipBehavior.lookAt(target);
    } else {
      // Fallback: direct sprite flip
      const shouldFaceLeft = target.x < this.entity.x;
      this.entity.setFlipX(shouldFaceLeft);
    }
  }

  /**
   * Enter guard state - stop moving and prepare to shoot
   */
  private enterGuardState(): void {
    if (this.isGuarding) return;
    this.isGuarding = true;
    this.updateShooterState();

    // Stop movement - check walker via namespaced state
    const walkerState = this.entity.state.behaviors.walker;
    if (walkerState) {
      const walker = this.entity.getBehavior(WalkerBehavior);
      walker?.stop();
    }

    // Set movement state to guarding
    this.updateEntityMovementState('guarding');
  }

  /**
   * Exit guard state - resume normal behavior
   */
  private exitGuardState(): void {
    if (!this.isGuarding) return;
    this.isGuarding = false;
    this.updateShooterState();

    // Resume movement - check walker via namespaced state
    const walkerState = this.entity.state.behaviors.walker;
    if (walkerState) {
      const walker = this.entity.getBehavior(WalkerBehavior);
      walker?.resume();
    }

    // Set movement state back to walking
    this.updateEntityMovementState('walking');
  }

  /**
   * Start chasing the target (for chaseWhileShooting mode)
   * Supports both Walker (ground) and Flyer (air) behaviors via namespaced state
   */
  private startChasing(target: Entity): void {
    // Check for flyer first via namespaced state
    const flyerState = this.entity.state.behaviors.flyer;
    if (flyerState?.isFlying) {
      const flyer = this.entity.getBehavior(FlyerBehavior);
      if (flyer) {
        flyer.flyToward(target);
        return;
      }
    }

    // Check for walker via namespaced state
    const walkerState = this.entity.state.behaviors.walker;
    if (walkerState && !walkerState.isChasing) {
      const walker = this.entity.getBehavior(WalkerBehavior);
      walker?.startChasing(target);
    }
  }

  /**
   * Stop chasing and return to patrol/hover
   * Supports both Walker (ground) and Flyer (air) behaviors via namespaced state
   */
  private stopChasing(): void {
    // Check for flyer first via namespaced state
    const flyerState = this.entity.state.behaviors.flyer;
    if (flyerState?.isFlying) {
      const flyer = this.entity.getBehavior(FlyerBehavior);
      if (flyer) {
        flyer.stopAndHover();
        return;
      }
    }

    // Check for walker via namespaced state
    const walkerState = this.entity.state.behaviors.walker;
    if (walkerState?.isChasing) {
      const walker = this.entity.getBehavior(WalkerBehavior);
      if (walker) {
        walker.stopChasing();
        walker.startPatrol();
      }
    }
  }

  /**
   * Check if can shoot (cooldown passed)
   */
  canShoot(time: number): boolean {
    return time > this.lastFired + this.fireRate;
  }

  /**
   * Get time until next shot
   */
  getTimeUntilNextShot(time: number): number {
    const nextShot = this.lastFired + this.fireRate;
    return Math.max(0, nextShot - time);
  }

  /**
   * Shoot toward a target position
   */
  shoot(time: number, targetX: number, targetY: number): boolean {
    if (!this.canShoot(time)) {
      return false;
    }

    this.lastFired = time;

    // Update namespaced state - isShooting
    const shooterState = this.entity.state.behaviors.shooter;
    if (shooterState) {
      shooterState.isShooting = true;
    }

    // Set entity action state to attacking
    this.updateEntityActionState('attacking');

    // Get fire positions from imagePoint or legacy firePoints
    const firePositions: { x: number; y: number }[] = [];

    if (this.fireImagePoint) {
      // Use imagePoint for fire position (handles rotation automatically)
      const point = this.entity.getImagePoint(this.fireImagePoint);
      if (point) {
        firePositions.push(point);
      } else {
        console.error(`[ShooterBehavior] ImagePoint "${this.fireImagePoint}" not found`, {
          entity: this.entity.entityType,
          texture: this.entity.texture?.key,
        });
        return false;
      }
    } else {
      // Use legacy firePoints array (static offsets, no rotation support)
      for (const point of this.firePoints) {
        firePositions.push({
          x: this.entity.x + point.x * (this.entity.flipX ? -1 : 1),
          y: this.entity.y + point.y,
        });
      }
    }

    // Spawn projectile from each fire position
    for (const pos of firePositions) {
      // Calculate angle toward target
      const angle = Phaser.Math.Angle.Between(pos.x, pos.y, targetX, targetY);
      const angleDeg = Phaser.Math.RadToDeg(angle);

      // Emit event for projectile system to handle
      const event: ProjectileSpawnEvent = {
        type: this.projectileType,
        x: pos.x,
        y: pos.y,
        angle: angleDeg,
        damage: this.damage,
        speed: this.projectileSpeed,
        allegiance: this.entity.allegiance,
        shooterId: (this.entity as Entity & { entityId?: number }).entityId
      };
      this.scene.events.emit('spawnProjectile', event);
    }

    // Call entity callback if exists
    if (typeof (this.entity as Entity & { onShoot?: () => void }).onShoot === 'function') {
      (this.entity as Entity & { onShoot: () => void }).onShoot();
    }

    // Reset action state after a short delay (attack animation duration)
    this.scene.time.delayedCall(200, () => {
      this.updateEntityActionState('none');
      // Reset isShooting in namespaced state
      const state = this.entity.state.behaviors.shooter;
      if (state) {
        state.isShooting = false;
      }
    });

    return true;
  }

  /**
   * Shoot toward an entity
   */
  shootAt(time: number, target: Entity): boolean {
    return this.shoot(time, target.x, target.y);
  }

  /**
   * Shoot in the direction the entity is facing
   */
  shootForward(time: number, distance: number = 1000): boolean {
    const direction = this.entity.flipX ? -1 : 1;
    const targetX = this.entity.x + direction * distance;
    return this.shoot(time, targetX, this.entity.y);
  }

  /**
   * Shoot at an angle
   */
  shootAtAngle(time: number, angle: number, distance: number = 1000): boolean {
    const targetX = this.entity.x + Math.cos(angle) * distance;
    const targetY = this.entity.y + Math.sin(angle) * distance;
    return this.shoot(time, targetX, targetY);
  }

  /**
   * Burst fire - shoot multiple times with short delays
   */
  burstFire(
    time: number,
    targetX: number,
    targetY: number,
    burstCount: number,
    burstDelay: number
  ): void {
    let shotsFired = 0;

    const fireNext = () => {
      if (shotsFired >= burstCount) return;

      // Force fire (ignore cooldown for burst)
      const originalLastFired = this.lastFired;
      this.lastFired = 0;
      this.shoot(time + shotsFired * burstDelay, targetX, targetY);
      this.lastFired = originalLastFired;

      shotsFired++;

      if (shotsFired < burstCount) {
        this.scene.time.delayedCall(burstDelay, fireNext);
      } else {
        // Set cooldown after burst completes
        this.lastFired = time + burstCount * burstDelay;
      }
    };

    fireNext();
  }

  /**
   * Set fire rate
   */
  setFireRate(rate: number): void {
    this.fireRate = rate;
  }

  /**
   * Set damage
   */
  setDamage(damage: number): void {
    this.damage = damage;
  }

  /**
   * Set projectile type
   */
  setProjectileType(type: string): void {
    this.projectileType = type;
  }

  /**
   * Check if target is in range
   */
  isInRange(target: Entity, range: number): boolean {
    const distance = Phaser.Math.Distance.Between(
      this.entity.x, this.entity.y,
      target.x, target.y
    );
    return distance <= range;
  }

  /**
   * Get angle to target
   */
  getAngleTo(targetX: number, targetY: number): number {
    return Phaser.Math.Angle.Between(
      this.entity.x, this.entity.y,
      targetX, targetY
    );
  }

  /**
   * Update entity's action state if it supports the state system
   * (Unit and Structure have setAction, other entities may not)
   */
  private updateEntityActionState(state: ActionState): void {
    if (typeof (this.entity as Entity & { setAction?: (state: ActionState) => void }).setAction === 'function') {
      (this.entity as Entity & { setAction: (state: ActionState) => void }).setAction(state);
    }
  }

  /**
   * Update entity's movement state if it supports the state system
   */
  private updateEntityMovementState(state: MovementState): void {
    if (typeof (this.entity as Entity & { setMovement?: (state: MovementState) => void }).setMovement === 'function') {
      (this.entity as Entity & { setMovement: (state: MovementState) => void }).setMovement(state);
    }
  }

  // =========================================================================
  // Debug Visualization
  // =========================================================================

  private debugGraphics: Phaser.GameObjects.Graphics | null = null;
  private debugEnabled: boolean = false;

  /**
   * Enable or disable debug visualization
   */
  setDebug(enabled: boolean): void {
    this.debugEnabled = enabled;
    console.log(`[ShooterBehavior] Debug ${enabled ? 'ON' : 'OFF'} for ${this.entity.texture?.key}, detectionRange=${this.detectionRange}`);

    if (!enabled && this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }
  }

  /**
   * Draw debug visualization (call in update when debug is needed)
   */
  drawDebug(): void {
    if (!this.debugEnabled || this.detectionRange <= 0) {
      return;
    }

    // Create graphics if needed
    if (!this.debugGraphics) {
      this.debugGraphics = this.scene.add.graphics();
      this.debugGraphics.setDepth(LayerDepths.HUD);
    }

    // Clear previous frame
    this.debugGraphics.clear();

    // Draw detection range circle
    const color = this.isGuarding ? 0xff0000 : 0xffff00; // Red when guarding, yellow otherwise
    this.debugGraphics.lineStyle(2, color, 0.5);
    this.debugGraphics.strokeCircle(this.entity.x, this.entity.y, this.detectionRange);

    // Draw min range if set
    if (this.minRange > 0) {
      this.debugGraphics.lineStyle(2, 0x00ff00, 0.3);
      this.debugGraphics.strokeCircle(this.entity.x, this.entity.y, this.minRange);
    }

    // Draw line to current target
    if (this.currentTarget && this.isGuarding) {
      this.debugGraphics.lineStyle(2, 0xff0000, 0.7);
      this.debugGraphics.lineBetween(
        this.entity.x, this.entity.y,
        this.currentTarget.x, this.currentTarget.y
      );
    }
  }

  /**
   * Cleanup debug graphics and namespaced state
   */
  override destroy(): void {
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }

    // Clean up namespaced state
    delete this.entity.state.behaviors.shooter;

    super.destroy();
  }
}
